import React, { useState } from 'react';
import { Box, Typography, Popover, Table, TableBody, TableCell, TableHead, TableRow, Button, Tooltip, Chip, Collapse, Alert } from '@mui/material';

const KLEUR = { groen: '#16a34a', oranje: '#d97706', rood: '#dc2626', geen: '#cbd5e1', info: '#94a3b8', inactief: '#e2e8f0' };
const MAAND = ['jan', 'feb', 'mrt', 'apr', 'mei', 'jun', 'jul', 'aug', 'sep', 'okt', 'nov', 'dec'];
const korte = (p) => `${MAAND[Number(p.slice(5, 7)) - 1]} '${p.slice(2, 4)}`;
const eur = (v) => `€${Math.round(v || 0).toLocaleString('nl-NL')}`;
const uur = (v) => `${Number(v || 0).toLocaleString('nl-NL', { maximumFractionDigits: 1 })}u`;

function samenvatting(c) {
  if (!c || c.signaal === 'geen') return 'Geen offerteregels en geen ingeplande taken';
  if (c.signaal === 'inactief') return 'Project is afgerond of geannuleerd — planning wordt niet meer beoordeeld';
  const rood = Object.values(c.fasen).filter((f) => f.status === 'rood').map((f) => f.label.toLowerCase());
  const oranje = Object.values(c.fasen).filter((f) => f.status === 'oranje').map((f) => f.label.toLowerCase());
  const delen = [];
  if (rood.length) delen.push(`${rood.join(', ')}: planning wijkt sterk af van offerte`);
  if (oranje.length) delen.push(`${oranje.join(', ')}: planning wijkt af`);
  if (!rood.length && !oranje.length && c.max_delta_pp >= 15) delen.push(`uren kloppen, maandverdeling wijkt tot ${c.max_delta_pp} pp af`);
  if (!delen.length) delen.push(`planning klopt met de offerte (${uur(c.planning.totaal_uren)} van ${uur(c.offerte.totaal_uren)})`);
  (c.waarschuwingen || []).filter((w) => w.ernst === 'oranje').forEach((w) => delen.push(w.tekst));
  return delen.join(' · ');
}

/**
 * Stip voor de projectnaam in de Revenue Forecast: kleur = planning-check (taken vs offerteregels per fase).
 * Klik = popover met de fasetabel, de voorgestelde maandverdeling naast de huidige en een knop om het voorstel over te nemen.
 */
function Onderbouwing({ rijen }) {
  if (!rijen?.length) return null;
  return (
    <Table size="small" sx={{ '& td, & th': { py: 0.4, fontSize: 11, verticalAlign: 'top' } }}>
      <TableHead>
        <TableRow>
          <TableCell>Maand</TableCell>
          <TableCell align="right">BBI</TableCell>
          <TableCell>Opbouw</TableCell>
          <TableCell>Taken en draaidagen die meetellen</TableCell>
        </TableRow>
      </TableHead>
      <TableBody>
        {rijen.map((r) => (
          <TableRow key={r.periode}>
            <TableCell sx={{ whiteSpace: 'nowrap', fontWeight: 600 }}>{korte(r.periode)}</TableCell>
            <TableCell align="right" sx={{ whiteSpace: 'nowrap' }}>{r.eur ? `${eur(r.eur)} (${r.pct}%)` : '–'}</TableCell>
            <TableCell sx={{ whiteSpace: 'nowrap', color: '#475569' }}>
              {['pre', 'productie', 'post', 'overig'].filter((f) => r.per_fase?.[f]).map((f) => `${f}: ${eur(r.per_fase[f])}`).join(' · ') || '–'}
            </TableCell>
            <TableCell>
              {(r.draaidagen || []).map((d, i) => (
                <Typography key={`d${i}`} sx={{ fontSize: 11, color: d.externen?.length ? '#b45309' : '#1e293b' }}>
                  🎬 {d.datum.slice(8, 10)}-{d.datum.slice(5, 7)} {d.naam}{d.crew?.length ? ` — ${d.crew.join(', ')}` : ''}
                  {d.externen?.length ? ` (extern: ${d.externen.join(', ')})` : ''}
                </Typography>
              ))}
              {(r.taken || []).map((t, i) => (
                <Typography key={`t${i}`} sx={{ fontSize: 11, color: t.afgerond ? '#94a3b8' : '#1e293b' }}>
                  {t.type} · {uur(t.uren)} · {t.datum.slice(8, 10)}-{t.datum.slice(5, 7)}{t.wie ? ` · ${t.wie}` : ''} — {t.naam}
                </Typography>
              ))}
              {!(r.taken || []).length && !(r.draaidagen || []).length && (
                <Typography sx={{ fontSize: 11, color: '#94a3b8' }}>voorbereiding zonder taken, of gelijkmatig gespreid</Typography>
              )}
            </TableCell>
          </TableRow>
        ))}
      </TableBody>
    </Table>
  );
}

/** Inhoud van de planning-check: fasetabel, maandverdeling, waarschuwingen en onderbouwing. Ook los te gebruiken in het projectvenster. */
export function PlanningCheckDetail({ check, onOvernemen, onSluit, compact = false }) {
  const [toonOnderbouwing, setToonOnderbouwing] = useState(false);
  const c = check || { signaal: 'geen' };
  const kleur = KLEUR[c.signaal] || KLEUR.geen;
  if (!check) {
    return <Typography sx={{ fontSize: 11.5, color: '#94a3b8' }}>Planning-check nog niet geladen.</Typography>;
  }
  if (c.signaal === 'geen') {
    return <Typography sx={{ fontSize: 11.5, color: '#94a3b8' }}>Geen offerteregels in Gripp en geen ingeplande taken voor dit project.</Typography>;
  }
  return (
        <Box sx={{ p: compact ? 0 : 2, width: compact ? '100%' : (toonOnderbouwing ? 820 : 520), maxWidth: compact ? '100%' : '92vw', transition: 'width .15s' }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
            <Box sx={{ width: 10, height: 10, borderRadius: '50%', bgcolor: kleur }} />
            <Typography sx={{ fontWeight: 600, fontSize: 13 }}>Planning versus offerte</Typography>
            <Typography sx={{ fontSize: 11, color: '#64748b', ml: 'auto' }}>
              {c.planning?.taken || 0} taken · {c.offerte?.regels || 0} offerteregels
            </Typography>
          </Box>
          <Typography sx={{ fontSize: 11, color: '#475569', mb: 1 }}>{samenvatting(c)}</Typography>
          {(c.waarschuwingen || []).map((w, i) => (
            <Alert key={i} severity={w.ernst === 'oranje' ? 'warning' : 'info'} sx={{ py: 0, mb: 1, fontSize: 11.5, '& .MuiAlert-message': { py: 0.5 } }}>{w.tekst}</Alert>
          ))}

          <Table size="small" sx={{ mb: 1.5, '& td, & th': { py: 0.4, fontSize: 11.5 } }}>
            <TableHead>
              <TableRow>
                <TableCell>Fase</TableCell>
                <TableCell align="right">Offerte</TableCell>
                <TableCell align="right">Ingepland</TableCell>
                <TableCell align="right">Verschil</TableCell>
                <TableCell align="right">Bedrag</TableCell>
                <TableCell />
              </TableRow>
            </TableHead>
            <TableBody>
              {['pre', 'productie', 'post'].map((f) => {
                const r = c.fasen?.[f];
                if (!r) return null;
                return (
                  <TableRow key={f}>
                    <TableCell sx={{ fontWeight: 500 }}>{r.label}</TableCell>
                    {f === 'productie' && (r.offerte_dagen > 0 || r.gepland_dagen > 0) ? (
                      <>
                        <TableCell align="right">{r.offerte_dagen}d<Typography component="span" sx={{ fontSize: 10, color: '#94a3b8' }}> ({uur(r.offerte_uren)})</Typography></TableCell>
                        <TableCell align="right">{r.gepland_dagen}d<Typography component="span" sx={{ fontSize: 10, color: '#94a3b8' }}> {r.dagen_bron === 'draaidagen' ? 'draaidagen' : 'taken'}</Typography></TableCell>
                        <TableCell align="right" sx={{ color: KLEUR[r.status] || '#64748b', fontWeight: r.status === 'groen' || r.status === 'geen' ? 400 : 600 }}>
                          {r.status === 'geen' ? '–' : `${r.gepland_dagen - r.offerte_dagen > 0 ? '+' : ''}${r.gepland_dagen - r.offerte_dagen}d`}
                        </TableCell>
                      </>
                    ) : (
                      <>
                        <TableCell align="right">{uur(r.offerte_uren)}</TableCell>
                        <TableCell align="right">{uur(r.gepland_uren)}<Typography component="span" sx={{ fontSize: 10, color: '#94a3b8' }}> ({r.taken})</Typography></TableCell>
                        <TableCell align="right" sx={{ color: KLEUR[r.status] || '#64748b', fontWeight: r.status === 'groen' || r.status === 'geen' || r.status === 'info' ? 400 : 600 }}>
                          {r.status === 'geen' ? '–' : r.status === 'info' ? 'geen taken' : `${r.delta_uren > 0 ? '+' : ''}${uur(r.delta_uren)}`}
                        </TableCell>
                      </>
                    )}
                    <TableCell align="right">{r.offerte_eur ? eur(r.offerte_eur) : '–'}</TableCell>
                    <TableCell><Box sx={{ width: 8, height: 8, borderRadius: '50%', bgcolor: KLEUR[r.status] || KLEUR.geen }} /></TableCell>
                  </TableRow>
                );
              })}
              {(c.offerte?.eur_projectleiding_overig > 0 || c.offerte?.eur_spreid > 0) && (
                <TableRow>
                  <TableCell colSpan={4} sx={{ color: '#64748b' }}>
                    Projectleiding en overig{c.offerte.eur_spreid > 0 ? ' · kanaalbeheer gelijkmatig over de looptijd' : ''}
                  </TableCell>
                  <TableCell align="right" sx={{ color: '#64748b' }}>{eur((c.offerte.eur_projectleiding_overig || 0) + (c.offerte.eur_spreid || 0))}</TableCell>
                  <TableCell />
                </TableRow>
              )}
            </TableBody>
          </Table>

          {c.verschil_maanden?.length > 0 && (
            <>
              <Typography sx={{ fontWeight: 600, fontSize: 12, mb: 0.5 }}>
                Wanneer valt de BBI volgens de planning?
                {c.voorstel?.ongepland_pct > 0 && (
                  <Chip size="small" label={`${c.voorstel.ongepland_pct}% nog niet ingepland (${eur(c.voorstel.ongepland_eur)})`} sx={{ ml: 1, height: 18, fontSize: 10, bgcolor: '#fef3c7', color: '#92400e' }} />
                )}
              </Typography>
              <Table size="small" sx={{ mb: 1.5, '& td, & th': { py: 0.3, fontSize: 11.5, px: 0.75 } }}>
                <TableHead>
                  <TableRow>
                    <TableCell />
                    {c.verschil_maanden.map((m) => <TableCell key={m.periode} align="right">{korte(m.periode)}</TableCell>)}
                  </TableRow>
                </TableHead>
                <TableBody>
                  <TableRow>
                    <TableCell sx={{ color: '#64748b' }}>Planning</TableCell>
                    {c.verschil_maanden.map((m) => <TableCell key={m.periode} align="right" sx={{ fontWeight: 600 }}>{m.voorstel ? `${m.voorstel}%` : '–'}</TableCell>)}
                  </TableRow>
                  <TableRow>
                    <TableCell sx={{ color: '#64748b' }}>Nu in tool</TableCell>
                    {c.verschil_maanden.map((m) => <TableCell key={m.periode} align="right">{m.huidig ? `${m.huidig}%` : '–'}</TableCell>)}
                  </TableRow>
                  <TableRow>
                    <TableCell sx={{ color: '#64748b' }}>Verschil</TableCell>
                    {c.verschil_maanden.map((m) => (
                      <TableCell key={m.periode} align="right" sx={{ color: Math.abs(m.delta_pp) >= 15 ? '#dc2626' : Math.abs(m.delta_pp) >= 5 ? '#d97706' : '#94a3b8' }}>
                        {m.delta_pp ? `${m.delta_pp > 0 ? '+' : ''}${m.delta_pp}` : '–'}
                      </TableCell>
                    ))}
                  </TableRow>
                </TableBody>
              </Table>
            </>
          )}

          {c.onderbouwing?.length > 0 && (
            <Box sx={{ mb: 1.5 }}>
              <Button size="small" onClick={() => setToonOnderbouwing((v) => !v)} sx={{ textTransform: 'none', fontSize: 11, px: 0.5 }}>
                {toonOnderbouwing ? 'Onderbouwing verbergen' : 'Onderbouwing per maand tonen'}
              </Button>
              <Collapse in={toonOnderbouwing}>
                <Box sx={{ maxHeight: 360, overflow: 'auto', border: '1px solid #e2e8f0', borderRadius: 1 }}>
                  <Onderbouwing rijen={c.onderbouwing} />
                </Box>
              </Collapse>
            </Box>
          )}
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 1 }}>
            <Typography sx={{ fontSize: 10.5, color: '#94a3b8' }}>
              Productie op draaidagen (offerte: dagprijsregels crew), pre en post op uren uit uur- en dagregels; taken uit Airtable op startdatum uitvoerder.
              Afspraken, intern en vrije dagen tellen niet mee. Externe crew = Freelancer of Bedrijf in Personen.
            </Typography>
            {onOvernemen && Object.keys(c.voorstel?.pct || {}).length > 0 && (
              <Button size="small" variant="contained" onClick={() => { onOvernemen(c.voorstel.pct); onSluit?.(); }} sx={{ textTransform: 'none', fontSize: 11, whiteSpace: 'nowrap' }}>
                Verdeling overnemen
              </Button>
            )}
          </Box>
        </Box>
  );
}

export default function PlanningCheck({ check, onOvernemen, laden, onClick, actief = false }) {
  const [anchor, setAnchor] = useState(null);
  if (laden && !check) {
    return <Box component="span" sx={{ display: 'inline-block', width: 8, height: 8, borderRadius: '50%', bgcolor: '#e2e8f0', mr: 0.75, flexShrink: 0 }} />;
  }
  const c = check || { signaal: 'geen' };
  const kleur = KLEUR[c.signaal] || KLEUR.geen;
  const open = Boolean(anchor);
  return (
    <>
      <Tooltip title={samenvatting(c)} placement="top">
        <Box
          component="button"
          type="button"
          aria-label={`Planning-check: ${c.signaal}`}
          onClick={(e) => { e.stopPropagation(); if (c.signaal === 'geen') return; if (onClick) onClick(); else setAnchor(e.currentTarget); }}
          data-signaal={c.signaal}
          sx={{
            all: 'unset', display: 'inline-block', width: 8, height: 8, borderRadius: '50%', bgcolor: kleur, mr: 0.75, flexShrink: 0,
            cursor: c.signaal === 'geen' ? 'default' : 'pointer', outline: (open || actief) ? `2px solid ${kleur}55` : 'none', outlineOffset: 1,
            boxShadow: c.signaal === 'rood' ? `0 0 0 2px ${kleur}33` : 'none',
          }}
        />
      </Tooltip>
      <Popover open={open} anchorEl={anchor} onClose={() => setAnchor(null)} anchorOrigin={{ vertical: 'bottom', horizontal: 'left' }} onClick={(e) => e.stopPropagation()}>
        <PlanningCheckDetail check={c} onOvernemen={onOvernemen} onSluit={() => setAnchor(null)} />
      </Popover>
    </>
  );
}
